import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Close from '../../assets/icons/close.svg';
import LanguageIcon from '../../assets/icons/language.svg';
import LogoCpo from '../../assets/icons/logo-cpo-black.svg';
import LogoColor from '../../assets/icons/logo-color.svg';
import { normalizeLocale } from '../../utils/helpers/constants.helpers';

import './LanguageSwitcher.scss';

const LANGUAGES = [
  { name: 'English', code: 'en' },
  { name: 'Français', code: 'fr' },
  { name: 'English US', code: 'en-US' },
  { name: 'Deutsch', code: 'de' },
  { name: 'Italiano', code: 'it' },
  { name: '简体中文', code: 'zh-Hans' },
  { name: '繁體中文', code: 'zh-Hant' },
  { name: '日本語', code: 'ja' },
  { name: 'Español', code: 'es' },
  { name: 'Português', code: 'pt-BR' },
  { name: '한국어', code: 'ko' },
  { name: 'Русский', code: 'ru' },
  { name: 'عربي', code: 'ar' },
  { name: 'Türkçe', code: 'tr' },
  { name: 'Bahasa Indonesia', code: 'id' },
  { name: 'ภาษาไทย', code: 'th' },
  { name: 'فارسی', code: 'fa' },
  { name: 'Nederlands', code: 'nl' },
  { name: 'Tiếng Việt', code: 'vi' },
  { name: 'हिंदी', code: 'hi' },
  { name: 'Polski', code: 'pl' },
  { name: 'עברית', code: 'he' },
  { name: 'Bahasa Malaysia', code: 'ms' },
];

const LanguageSwitcher = ({ rightToLeft, isCpo }) => {
  const { t, i18n } = useTranslation();
  const [isLanguagesOpen, setIsLanguagesOpen] = useState(false);
  const [isChangingLanguage, setIsChangingLanguage] = useState(false);
  const triggerButtonRef = useRef(null);
  const closeButtonRef = useRef(null);
  const dialogId = useId();

  const selectedLanguage = normalizeLocale(
    i18n.resolvedLanguage || i18n.language,
  );

  const openLanguages = () => {
    setIsLanguagesOpen(true);
  };

  const closeLanguages = () => {
    setIsLanguagesOpen(false);
  };

  useEffect(() => {
    if (!isLanguagesOpen) {
      return undefined;
    }

    closeButtonRef.current?.focus();

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeLanguages();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isLanguagesOpen]);

  useEffect(() => {
    if (!isLanguagesOpen) {
      triggerButtonRef.current?.focus();
    }
  }, [isLanguagesOpen]);

  const handleLanguageChange = (languageCode) => {
    if (isChangingLanguage) {
      return;
    }

    const locale = normalizeLocale(languageCode);

    if (locale === selectedLanguage) {
      closeLanguages();
      return;
    }

    setIsChangingLanguage(true);

    // Persist the canonical i18next locale before the new app instance loads.
    localStorage.setItem('i18nextLng', locale);

    // Reload immediately. The new instance of i18next detects i18nextLng.
    window.location.reload();
  };

  const triggerClassName = rightToLeft ? 'langButtonRTL' : 'langButton';
  const sliderClassName = rightToLeft ? 'languageSliderRTL' : 'languageSlider';
  const blockDirection = rightToLeft ? 'rtl' : 'ltr';
  const changeLanguageLabel = t('changeLanguage', {
    defaultValue: 'Change language',
  });
  const closeLanguageMenuLabel = t('closeLanguageMenu', {
    defaultValue: 'Close language selector',
  });

  return (
    <>
      {!isLanguagesOpen && (
        <button
          ref={triggerButtonRef}
          id={`${dialogId}-trigger`}
          type="button"
          className={triggerClassName}
          aria-expanded={false}
          aria-controls={dialogId}
          aria-label={changeLanguageLabel}
          onClick={openLanguages}
        >
          <img src={LanguageIcon} alt="" aria-hidden="true" />
        </button>
      )}

      {isLanguagesOpen && (
        <button
          type="button"
          className="overlayShown"
          aria-label={closeLanguageMenuLabel}
          onClick={closeLanguages}
        />
      )}

      <div
        id={dialogId}
        className={`${sliderClassName} ${isLanguagesOpen ? 'slide-in' : ''}`}
        dir={blockDirection}
        aria-hidden={!isLanguagesOpen}
      >
        {isLanguagesOpen && (
          <nav
            className="navigation"
            aria-label={changeLanguageLabel}
            role="dialog"
            aria-modal="true"
          >
            <button
              ref={closeButtonRef}
              type="button"
              className="closeButton"
              aria-label={closeLanguageMenuLabel}
              onClick={closeLanguages}
            >
              <img src={Close} alt="" aria-hidden="true" />
            </button>

            <img
              className={isCpo ? 'logoCpo' : 'logoNav'}
              src={isCpo ? LogoCpo : LogoColor}
              alt={t('accessibility.logoImgAlt')}
            />

            <ul aria-labelledby={`${dialogId}-title`}>
              <li className="heading">
                <h2 id={`${dialogId}-title`}>{t('languages')}</h2>
              </li>

              {LANGUAGES.map((language) => {
                const isSelected = selectedLanguage === language.code;

                return (
                  <li key={language.code}>
                    <button
                      type="button"
                      lang={language.code}
                      className={isSelected ? 'selectedLanguage' : undefined}
                      aria-current={isSelected ? 'true' : undefined}
                      disabled={isChangingLanguage}
                      onClick={() => handleLanguageChange(language.code)}
                    >
                      {language.name}
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>
        )}
      </div>
    </>
  );
};

export default LanguageSwitcher;
